import React, { useState } from "react";
import { Button } from "@mui/material";
import MailService from "../../services/MailService";
import GeneralStrings from "../../strings/GeneralStrings.json";
import AlertStrings from "../../strings/AlertStrings.json";
import Footer from "../../components/Footer/Footer";
import * as Yup from "yup";
import "yup-phone";
import { Alert } from "reactstrap";
import Recaptcha from "react-recaptcha";
import { Helmet } from "react-helmet";
import { ErrorMessage, Field, Form, Formik } from "formik";
import GeneralConst from "../../consts/GeneralConst";
import serviceAreaImage from "../../assets/images/service-area-map.png";
import HomepageNavbar from "../../components/HomepageNavbar/HomepageNavbar";
import "./ServiceAreaPage.scss";
import {
  validateEmail,
  validateMessage,
  validateName,
} from "../../utilities/ValidationHelper";
import { Card, CardActionArea, CardMedia } from "@mui/material";

const ServiceAreaPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [sentMessage, setSentMessage] = useState(false);
  const [failedMessage, setFailedMessage] = useState(false);
  const [recaptchaLoad, setRecaptchaLoad] = useState(false);
  const STATUS_CODES = GeneralConst.STATUS_CODES;
  const SERVICE_AREA_STRINGS = GeneralStrings.SERVICE_AREA;
  const TITLE = `${SERVICE_AREA_STRINGS.BANNER_HEADING} | ${GeneralConst.SITE_NAME}`;

  const recaptchaLoaded = () => {
    setRecaptchaLoad(true);
  };

  const verifiedRecaptcha = (response) => {
    if (response) {
      setIsVerified(true);
    }
  };

  const resetErrorMessages = () => {
    setSentMessage(false);
    setFailedMessage(false);
  };

  const digitsOnly = (value) => /^\d+$/.test(value);

  const ContactFormSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .required(AlertStrings.FORM_VALIDATION.PHONE_NUMBER_REQUIRED)
      .test(
        AlertStrings.FORM_VALIDATION.DIGITS_ONLY,
        AlertStrings.FORM_VALIDATION.NUMBERS_ONLY_ALLOWED,
        digitsOnly
      )
      .min(7, AlertStrings.FORM_VALIDATION.ENTER_PHONE_NUMBER)
      .max(10, AlertStrings.FORM_VALIDATION.PHONE_NUMBER_TOO_LONG),
  });

  const handleSubmit = (formValues) => {
    setIsLoading(true);
    resetErrorMessages();

    if (recaptchaLoad && isVerified) {
      MailService.submitContactForm(formValues)
        .then((response) => {
          if (response.data.status === STATUS_CODES.ERROR) {
            setFailedMessage(
              response?.data?.statusMessage || AlertStrings.GENERAL_ERROR
            );
          } else {
            setSentMessage(
              response?.data?.statusMessage || AlertStrings.GENERAL_SUCCESS
            );
          }
        })
        .catch((response) => {
          setFailedMessage(
            response?.data?.statusMessage || AlertStrings.GENERAL_ERROR
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    setIsLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <div className="service-area-page">
        <HomepageNavbar transparentNavbar={false} />
        <section className="page-banner">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-8 col-xl-7">
                <div className="banner-heading">
                  <h1>{SERVICE_AREA_STRINGS.BANNER_HEADING}</h1>
                  <p className="banner-subheading">
                    {SERVICE_AREA_STRINGS.BANNER_SUBTEXT}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="container">
          <div className="service-area-content row">
            <div className="service-area-image-container col-sm-12 col-md-6">
              <Card sx={{ maxWidth: 345 }}>
                <CardActionArea>
                  <CardMedia
                    component="img"
                    image={serviceAreaImage}
                    alt="Service Area"
                  />
                </CardActionArea>
              </Card>
            </div>
            <div className="service-area-details col-sm-12 col-md-6">
              <h4 className="heading">{SERVICE_AREA_STRINGS.HEADING}</h4>
              <ul
                className="service-area-list"
                dangerouslySetInnerHTML={{
                  __html: SERVICE_AREA_STRINGS.SERVICE_AREA_LIST_HTML,
                }}
              />
              <h5 className="subheading">{SERVICE_AREA_STRINGS.SUBHEADING}</h5>
              <hr />
              <div className="form-container">
                {failedMessage && <Alert color="danger">{failedMessage}</Alert>}
                {sentMessage && <Alert>{sentMessage}</Alert>}
                <Formik
                  initialValues={{
                    name: "",
                    email: "",
                    phoneNumber: "",
                    message: "",
                  }}
                  validationSchema={ContactFormSchema}
                  onSubmit={(values) => {
                    handleSubmit(values);
                  }}
                >
                  <Form>
                    <div className="form-group">
                      <Field
                        name="name"
                        className="form-control"
                        placeholder={GeneralStrings.FORM.FORM_LABELS.YOUR_NAME}
                        validate={validateName}
                      />
                      <ErrorMessage
                        component="span"
                        className="form-error"
                        name="name"
                      />
                    </div>
                    <div className="form-group">
                      <Field
                        name="email"
                        className="form-control"
                        placeholder={GeneralStrings.FORM.FORM_LABELS.YOUR_EMAIL}
                        validate={validateEmail}
                      />
                      <ErrorMessage
                        component="span"
                        className="form-error"
                        name="email"
                      />
                    </div>
                    <div className="form-group">
                      <Field
                        name="phoneNumber"
                        className="form-control"
                        placeholder={
                          GeneralStrings.FORM.FORM_LABELS.PHONE_NUMBER
                        }
                      />
                      <ErrorMessage
                        component="span"
                        className="form-error"
                        name="phoneNumber"
                      />
                    </div>
                    <div className="form-group">
                      <Field
                        as="textarea"
                        name="message"
                        className="form-control"
                        placeholder={GeneralStrings.FORM.FORM_LABELS.MESSAGE}
                        validate={validateMessage}
                      />
                      <ErrorMessage
                        component="span"
                        className="form-error"
                        name="message"
                      />
                    </div>
                    <div className="form-group">
                      <Recaptcha
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        render="explicit"
                        onloadCallback={recaptchaLoaded}
                        verifyCallback={verifiedRecaptcha}
                      />
                    </div>
                    <div className="form-group">
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={isLoading}
                        loading={isLoading}
                        className="submit-btn"
                        type="submit"
                      >
                        {GeneralStrings.SUBMIT}
                      </Button>
                    </div>
                  </Form>
                </Formik>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default ServiceAreaPage;
